import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import PageTitle from "../../layouts/PageTitle";
import {
  getReclamationsByRestaurant,
  updateReclamationStatus,
  getReclamationById,
  archiveReclamation,
} from "../../../services/ClientReclamationService";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import config from "../../../../config.json";
import { fetchConnectedUser } from "../../../services/OrdersService";
import socket from "../../../services/socket";
import "react-datepicker/dist/react-datepicker.css";
import "./ClientReclamations.css";

const PAGE_SIZE = 8;

const CLAIM_TYPE_OPTIONS = [
  { value: "food", labelKey: "claimCategory_food", fallback: "Food", icon: "fa-cutlery" },
  { value: "service", labelKey: "claimCategory_service", fallback: "Service", icon: "fa-handshake-o" },
  { value: "application", labelKey: "claimCategory_application", fallback: "Application", icon: "fa-mobile" },
  { value: "hygiene", labelKey: "claimCategory_hygiene", fallback: "Hygiene", icon: "fa-tint" },
  { value: "staffing", labelKey: "claimCategory_staffing", fallback: "Staffing", icon: "fa-users" },
  { value: "safety", labelKey: "claimCategory_safety", fallback: "Safety", icon: "fa-shield" },
  { value: "lost_object", labelKey: "claimCategory_lost_object", fallback: "Lost object", icon: "fa-search" },
  { value: "supply", labelKey: "claimCategory_supply", fallback: "Supply", icon: "fa-cube" },
  { value: "management", labelKey: "claimCategory_management", fallback: "Management", icon: "fa-briefcase" },
  { value: "other", labelKey: "claimCategory_other", fallback: "Other", icon: "fa-tag" },
];

const KNOWN_TYPE_VALUES = new Set(CLAIM_TYPE_OPTIONS.map((o) => o.value));

const TYPE_ICON_MAP = CLAIM_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.icon;
  return acc;
}, {});

const STATUS_OPTIONS = [
  { value: "pending", labelKey: "reclamationStatus_pending", fallback: "Pending" },
  { value: "in_progress", labelKey: "reclamationStatus_in_progress", fallback: "In progress" },
  { value: "resolved", labelKey: "reclamationStatus_resolved", fallback: "Resolved" },
];

const getInitials = (email = "", name = "") => {
  const source = (name && name !== "-" ? name : email || "").trim();
  if (!source) return "?";
  if (source.includes("@")) {
    const local = source.split("@")[0];
    const parts = local.split(/[._\-\s]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
  }
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const formatDateShort = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateLong = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const toDateParam = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildWhatsAppLink = (phone, message) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");
  if (!cleanPhone) return "";
  return `https://wa.me/${cleanPhone.replace(/^\+/, "")}?text=${encodeURIComponent(message)}`;
};

const buildGmailComposeLink = (email, subject, body) => {
  const clean = String(email || "").trim();
  if (!clean) return "";
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: clean,
  });
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
};

const buildClaimReplyContent = (reclamation, t, extractClientName) => {
  const clientName = extractClientName(reclamation);
  const greetingName = clientName !== "-" ? clientName : "";
  return {
    subject: t("claimsReplySubject") || "Reply to your claim",
    emailBody:
      t("claimsReplyEmailBody", { name: greetingName || "" }) ||
      `Hello ${greetingName},\n\nRegarding your claim,\n\n`,
    whatsappMessage:
      t("claimsReplyWhatsAppBody", { name: greetingName || "" }) ||
      `Hello ${greetingName}, regarding your claim:`,
  };
};

function ClaimDetailsModal({
  open,
  reclamation,
  onClose,
  t,
  extractClientName,
  extractDescriptionText,
  buildWhatsAppLink,
  renderTypeBadges,
  renderStatusSelect,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !reclamation) return null;

  const clientName = extractClientName(reclamation);
  const emailValue = (reclamation.email || reclamation.user?.email || "").trim();
  const phoneValue = (reclamation.phone || reclamation.user?.phone || "").trim();
  const description = extractDescriptionText(reclamation.description);
  const displayName =
    clientName !== "-" ? clientName : emailValue || t("claims") || "Claim";
  const { subject, emailBody, whatsappMessage } = buildClaimReplyContent(
    reclamation,
    t,
    extractClientName
  );
  const emailHref = buildGmailComposeLink(emailValue, subject, emailBody);
  const whatsappHref = buildWhatsAppLink(phoneValue, whatsappMessage);
  const imageUrls = (() => {
    const names =
      Array.isArray(reclamation.images) && reclamation.images.length > 0
        ? reclamation.images
        : reclamation.image
          ? [reclamation.image]
          : [];
    return names
      .filter(Boolean)
      .map((name) => `${config.dev}/uploads/client-reclamation/${name}`);
  })();

  return createPortal(
    <div className="cr-modal-backdrop" onClick={onClose}>
      <div
        className="cr-modal cr-details"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-details-title"
      >
        <header className="cr-details__head">
          <h2 id="cr-details-title">
            {t("Reclamation Details") || "Claim details"}
          </h2>
          <button
            type="button"
            className="cr-modal__close"
            onClick={onClose}
            aria-label={t("close") || "Close"}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>

        <div className="cr-details__body">
          <section className="cr-details__summary">
            <span className="cr-details__avatar" aria-hidden="true">
              {getInitials(emailValue, clientName)}
            </span>
            <div className="cr-details__summary-main">
              <div className="cr-details__summary-top">
                <span className="cr-details__name">{displayName}</span>
                <span className="cr-details__date">
                  <i className="fa fa-calendar-o" aria-hidden="true" />
                  {formatDateLong(reclamation.createdAt)}
                </span>
              </div>
              <div className="cr-details__chips">
                {renderTypeBadges(reclamation)}
                {renderStatusSelect(reclamation)}
              </div>
            </div>
          </section>

          <section className="cr-details__block">
            <h3 className="cr-details__block-title">
              <i className="fa fa-user" aria-hidden="true" />
              {t("claimsContact") || "Contact"}
            </h3>
            <div className="cr-details__list">
              <div className="cr-details__list-item">
                <div className="cr-details__list-left">
                  <span className="cr-details__list-icon is-email" aria-hidden="true">
                    <i className="fa fa-envelope-o" />
                  </span>
                  <div>
                    <span className="cr-details__list-label">{t("Email") || "Email"}</span>
                    <span className="cr-details__list-value" title={emailValue || "—"}>
                      {emailValue || "—"}
                    </span>
                  </div>
                </div>
                <a
                  className={`cr-details__mini-btn is-gmail ${!emailHref ? "is-disabled" : ""}`}
                  href={emailHref || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!emailHref}
                  onClick={(e) => {
                    if (!emailHref) e.preventDefault();
                  }}
                >
                  <i className="fa-brands fa-google" aria-hidden="true" />
                  {t("claimsGmail") || "Gmail"}
                </a>
              </div>

              <div className="cr-details__list-item">
                <div className="cr-details__list-left">
                  <span className="cr-details__list-icon is-phone" aria-hidden="true">
                    <i className="fa fa-phone" />
                  </span>
                  <div>
                    <span className="cr-details__list-label">
                      {t("Phone Number") || "Phone"}
                    </span>
                    <span className="cr-details__list-value" title={phoneValue || "—"}>
                      {phoneValue || "—"}
                    </span>
                  </div>
                </div>
                <a
                  className={`cr-details__mini-btn is-whatsapp ${
                    !whatsappHref ? "is-disabled" : ""
                  }`}
                  href={whatsappHref || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!whatsappHref}
                  onClick={(e) => {
                    if (!whatsappHref) e.preventDefault();
                  }}
                >
                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                  WhatsApp
                </a>
              </div>
            </div>
          </section>

          {imageUrls.length > 0 ? (
            <section className="cr-details__block">
              <h3 className="cr-details__block-title">
                <i className="fa fa-image" aria-hidden="true" />
                {imageUrls.length > 1
                  ? t("Images") || "Images"
                  : t("Image") || "Image"}
              </h3>
              <div className={`cr-details__media-grid${imageUrls.length === 1 ? " is-single" : ""}`}>
                {imageUrls.map((url, index) => (
                  <a
                    key={`${url}-${index}`}
                    className="cr-details__media"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={url}
                      alt={`${t("Claim attachment") || "Claim attachment"} ${index + 1}`}
                    />
                  </a>
                ))}
              </div>
            </section>
          ) : null}

          <section className="cr-details__block">
            <h3 className="cr-details__block-title">
              <i className="fa fa-align-left" aria-hidden="true" />
              {t("Description") || "Description"}
            </h3>
            <p className="cr-details__text">{description || "—"}</p>
          </section>

          {reclamation.recommendation ? (
            <section className="cr-details__block">
              <h3 className="cr-details__block-title">
                <i className="fa fa-lightbulb-o" aria-hidden="true" />
                {t("Recommendation") || "Recommendation"}
              </h3>
              <p className="cr-details__text is-reco">{reclamation.recommendation}</p>
            </section>
          ) : null}
        </div>

        <footer className="cr-details__foot">
          <button type="button" className="cr-modal__btn cr-modal__btn--ghost" onClick={onClose}>
            {t("close") || "Close"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

const ClientReclamations = () => {
  const { t } = useTranslation();
  const [reclamations, setReclamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const extractDescriptionText = (rawDescription = "") => {
    const descriptionValue = (rawDescription || "").trim();
    if (!descriptionValue) return "-";
    const marker = "Description:";
    const markerIndex = descriptionValue.indexOf(marker);
    if (markerIndex === -1) return descriptionValue;
    const extracted = descriptionValue.slice(markerIndex + marker.length).trim();
    return extracted || descriptionValue;
  };

  const extractClientName = (reclamation) => {
    const directName = (reclamation?.clientName || "").trim();
    if (directName) return directName;
    const userFirst = (reclamation?.user?.firstName || "").trim();
    const userLast = (reclamation?.user?.lastName || "").trim();
    const userFullName = `${userFirst} ${userLast}`.trim();
    if (userFullName) return userFullName;
    const rawDescription = (reclamation?.description || "").trim();
    const marker = "Name:";
    const markerIndex = rawDescription.indexOf(marker);
    if (markerIndex === -1) return "-";
    const afterMarker = rawDescription.slice(markerIndex + marker.length);
    const nextSeparator = afterMarker.indexOf("|");
    const parsedName =
      nextSeparator === -1 ? afterMarker.trim() : afterMarker.slice(0, nextSeparator).trim();
    return parsedName || "-";
  };

  const fetchReclamations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getReclamationsByRestaurant(
        page,
        PAGE_SIZE,
        search,
        typeFilter,
        statusFilter,
        toDateParam(dateFrom),
        toDateParam(dateTo)
      );
      setReclamations(response.reclamations || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      toast.error(error.message || t("Failed to fetch reclamations") || "Failed to fetch reclamations");
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, dateFrom, dateTo, t]);

  useEffect(() => {
    fetchReclamations();
  }, [fetchReclamations]);

  useEffect(() => {
    let active = true;
    const restaurantIdRef = { current: null };

    const joinRestaurantRoom = () => {
      const id = restaurantIdRef.current;
      if (id != null && id !== "") {
        socket.emit("join-restaurant", String(id));
      }
    };

    const onClientReclamationChange = () => {
      if (!active) return;
      fetchReclamations();
    };

    const init = async () => {
      try {
        const user = await fetchConnectedUser();
        restaurantIdRef.current = user?.restaurantFK ?? null;
      } catch {
        restaurantIdRef.current = null;
      }
      if (!active) return;
      joinRestaurantRoom();
      socket.on("connect", joinRestaurantRoom);
      socket.on("new-client-reclamation", onClientReclamationChange);
    };

    init();

    return () => {
      active = false;
      socket.off("connect", joinRestaurantRoom);
      socket.off("new-client-reclamation", onClientReclamationChange);
    };
  }, [fetchReclamations]);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || [null, null];
    setDateFrom(start);
    setDateTo(end);
    setPage(1);
  };

  const getStatusLabel = useCallback(
    (status) => {
      const option = STATUS_OPTIONS.find((item) => item.value === status);
      return option ? t(option.labelKey) || option.fallback : status || "—";
    },
    [t]
  );

  const handleStatusChange = async (reclamation, newStatus) => {
    const id = reclamation?._id;
    const currentStatus = reclamation?.status || "pending";
    if (!id || !newStatus || newStatus === currentStatus) return;

    const statusLabel = getStatusLabel(newStatus);
    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text:
        t("claimsStatusChangeConfirm", { status: statusLabel }) ||
        `Change status to "${statusLabel}"?`,
      icon: "warning",
      buttons: [t("cancel") || "Cancel", t("confirm") || "Confirm"],
    });
    if (!ok) {
      // Re-render so controlled <select> snaps back to current status
      setReclamations((prev) => [...prev]);
      if (selectedReclamation?._id === id) {
        setSelectedReclamation((prev) => (prev ? { ...prev } : prev));
      }
      return;
    }

    setUpdatingStatusId(id);
    try {
      await updateReclamationStatus(id, newStatus);
      toast.success(t("Status updated successfully") || "Status updated successfully");

      setReclamations((prev) =>
        prev.map((rec) => (rec._id === id ? { ...rec, status: newStatus } : rec))
      );

      if (selectedReclamation?._id === id) {
        setSelectedReclamation((prev) => (prev ? { ...prev, status: newStatus } : prev));
      }
    } catch (error) {
      toast.error(error.message || t("Failed to update status") || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await getReclamationById(id);
      const reclamationData = response?.data || response || null;
      setSelectedReclamation(reclamationData);
      setShowDetailModal(true);
    } catch (error) {
      toast.error(
        error.message || t("Failed to fetch reclamation details") || "Failed to fetch reclamation details"
      );
    }
  };

  const handleArchive = async (reclamation) => {
    const id = reclamation?._id;
    if (!id) return;

    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text:
        t("claimsArchiveConfirm") ||
        "Archive this claim? It will be moved to Archives.",
      icon: "warning",
      buttons: [t("cancel") || "Cancel", t("confirm") || "Confirm"],
    });
    if (!ok) return;

    try {
      await archiveReclamation(id);
      toast.success(t("claimsArchived") || "Claim archived");
      if (selectedReclamation?._id === id) {
        setShowDetailModal(false);
        setSelectedReclamation(null);
      }
      await fetchReclamations();
    } catch (error) {
      toast.error(
        error.message || t("claimsArchiveError") || "Could not archive claim"
      );
    }
  };

  const getClaimTypes = (reclamation) => {
    if (Array.isArray(reclamation?.types) && reclamation.types.length > 0) {
      return reclamation.types;
    }
    return reclamation?.type ? [reclamation.type] : ["other"];
  };

  const getTypeLabel = (type, customType = "") => {
    if (type === "other" && customType) {
      return customType.trim();
    }
    const option = CLAIM_TYPE_OPTIONS.find((item) => item.value === type);
    return option ? t(option.labelKey) || option.fallback : type || "—";
  };

  const getTypeBadgeClass = (type) => {
    const normalized = String(type || "other")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    return KNOWN_TYPE_VALUES.has(normalized) ? normalized : "other";
  };

  const getTypeIcon = (type) => TYPE_ICON_MAP[getTypeBadgeClass(type)] || "fa-tag";

  const renderTypeBadge = (type, customType = "") => {
    const typeClass = getTypeBadgeClass(type);
    const label = getTypeLabel(type, customType);
    return (
      <span className={`cr-type is-${typeClass}`} title={label} key={`${typeClass}-${label}`}>
        <i className={`fa ${getTypeIcon(type)}`} aria-hidden="true" />
        <span>{label}</span>
      </span>
    );
  };

  const renderTypeBadges = (reclamation) => {
    const types = getClaimTypes(reclamation);
    return (
      <div className="cr-type-list">
        {types.map((type) => renderTypeBadge(type, reclamation?.customType))}
      </div>
    );
  };

  const renderStatusSelect = (reclamation) => {
    const status = reclamation?.status || "pending";
    const isUpdating = updatingStatusId === reclamation?._id;

    return (
      <div className={`cr-status-wrap is-${status}${isUpdating ? " is-updating" : ""}`}>
        <span className="cr-status-dot" aria-hidden="true" />
        <select
          key={`${reclamation?._id}-${status}`}
          className="cr-status-select"
          value={status}
          disabled={isUpdating}
          onChange={(e) => handleStatusChange(reclamation, e.target.value)}
          aria-label={t("status") || "Status"}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey) || option.fallback}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  return (
    <>
      <PageTitle
        motherMenu={t("Dashboard") || "Dashboard"}
        activeMenu={t("claims") || "Claims"}
      />

      <div className="cr-page">
        <div className="cr-card">
          <div className="cr-filters">
            <div className="cr-search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={t("claimsSearchPlaceholder") || "Search by email or phone..."}
              />
            </div>

            <div className="cr-select-wrap">
              <i className="fa fa-tags" aria-hidden="true" />
              <select
                className="cr-input"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t("claimsAllTypes") || "All types"}</option>
                {CLAIM_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey) || option.fallback}
                  </option>
                ))}
              </select>
            </div>

            <div className="cr-select-wrap">
              <i className="fa fa-flag" aria-hidden="true" />
              <select
                className="cr-input"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t("claimsAllStatus") || "All status"}</option>
                <option value="pending">{t("reclamationStatus_pending") || "Pending"}</option>
                <option value="in_progress">
                  {t("reclamationStatus_in_progress") || "In progress"}
                </option>
                <option value="resolved">{t("reclamationStatus_resolved") || "Resolved"}</option>
              </select>
            </div>

            <div
              className={`cr-date-range${dateFrom || dateTo ? " is-active" : ""}`}
              aria-label={t("claimsDateRange") || "Date range"}
            >
              <i className="fa fa-calendar" aria-hidden="true" />
              <DatePicker
                selected={dateFrom}
                onChange={handleDateRangeChange}
                startDate={dateFrom}
                endDate={dateTo}
                selectsRange
                maxDate={new Date()}
                placeholderText={t("claimsDateRangePlaceholder") || "Date A - Date B"}
                className="cr-date-range__input"
                dateFormat="dd/MM/yyyy"
                isClearable
                showPopperArrow={false}
              />
            </div>

            <button type="button" className="cr-refresh" onClick={fetchReclamations}>
              <i className="fa fa-refresh" aria-hidden="true" />
              {t("refresh") || "Refresh"}
            </button>
          </div>

          {loading ? (
            <div className="cr-loading">
              <span className="cr-spinner" aria-hidden="true" />
              {t("loading") || "Loading"}...
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table cr-table mb-0">
                  <thead>
                    <tr>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-user" aria-hidden="true" />
                          {t("claimsColClient") || "Client"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-phone" aria-hidden="true" />
                          {t("Phone") || "Phone"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-tags" aria-hidden="true" />
                          {t("Type") || "Type"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-flag" aria-hidden="true" />
                          {t("status") || "Status"}
                        </span>
                      </th>
                      <th>
                        <span className="cr-th">
                          <i className="fa fa-calendar" aria-hidden="true" />
                          {t("Date") || "Date"}
                        </span>
                      </th>
                      <th className="text-end">
                        <span className="cr-th cr-th--end">
                          <i className="fa fa-ellipsis-h" aria-hidden="true" />
                          {t("actions") || "Actions"}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reclamations.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="cr-empty">
                          <i className="fa fa-inbox" aria-hidden="true" />
                          <span>{t("No reclamations found") || "No reclamations found"}</span>
                        </td>
                      </tr>
                    ) : (
                      reclamations.map((reclamation) => {
                        const email =
                          reclamation.email || reclamation.user?.email || "—";
                        const phone =
                          reclamation.phone || reclamation.user?.phone || "—";
                        const name = extractClientName(reclamation);
                        const displayClientName =
                          name && name !== "-"
                            ? name
                            : t("claimsAnonymous") || "Anonymous";
                        const status = reclamation.status || "pending";
                        const emailValue =
                          (reclamation.email || reclamation.user?.email || "").trim();
                        const phoneValue =
                          (reclamation.phone || reclamation.user?.phone || "").trim();
                        const { subject, emailBody, whatsappMessage } =
                          buildClaimReplyContent(reclamation, t, extractClientName);
                        const gmailHref = buildGmailComposeLink(
                          emailValue,
                          subject,
                          emailBody
                        );
                        const whatsappHref = buildWhatsAppLink(
                          phoneValue,
                          whatsappMessage
                        );

                        return (
                          <tr key={reclamation._id} className={`cr-row is-${status}`}>
                            <td>
                              <div className="cr-client">
                                <span className="cr-avatar" aria-hidden="true">
                                  {getInitials(email, name)}
                                </span>
                                <div className="cr-client__meta">
                                  <span className="cr-client__name" title={displayClientName}>
                                    {displayClientName}
                                  </span>
                                  <span className="cr-email" title={email}>
                                    {email}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="cr-phone">
                                <i className="fa fa-phone" aria-hidden="true" />
                                <span>{phone}</span>
                              </span>
                            </td>
                            <td>{renderTypeBadges(reclamation)}</td>
                            <td>{renderStatusSelect(reclamation)}</td>
                            <td>
                              <span className="cr-date">
                                <i className="fa fa-calendar-o" aria-hidden="true" />
                                <span>{formatDateShort(reclamation.createdAt)}</span>
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="cr-row-actions">
                                <a
                                  className={`cr-icon-btn cr-icon-btn--gmail${
                                    !gmailHref ? " is-disabled" : ""
                                  }`}
                                  href={gmailHref || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-disabled={!gmailHref}
                                  aria-label={t("claimsActionGmail") || "Reply by Gmail"}
                                  title={t("claimsActionGmail") || "Reply by Gmail"}
                                  onClick={(e) => {
                                    if (!gmailHref) e.preventDefault();
                                  }}
                                >
                                  <i className="fa-brands fa-google" aria-hidden="true" />
                                </a>
                                <a
                                  className={`cr-icon-btn cr-icon-btn--whatsapp${
                                    !whatsappHref ? " is-disabled" : ""
                                  }`}
                                  href={whatsappHref || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-disabled={!whatsappHref}
                                  aria-label={
                                    t("claimsActionWhatsApp") || "Reply by WhatsApp"
                                  }
                                  title={
                                    t("claimsActionWhatsApp") || "Reply by WhatsApp"
                                  }
                                  onClick={(e) => {
                                    if (!whatsappHref) e.preventDefault();
                                  }}
                                >
                                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                                </a>
                                <button
                                  type="button"
                                  className="cr-icon-btn cr-icon-btn--view"
                                  onClick={() => handleViewDetails(reclamation._id)}
                                  aria-label={t("View") || "View"}
                                  title={t("View") || "View"}
                                >
                                  <i className="fa fa-eye" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="cr-icon-btn cr-icon-btn--archive"
                                  onClick={() => handleArchive(reclamation)}
                                  aria-label={t("claimsArchive") || "Archive"}
                                  title={t("claimsArchive") || "Archive"}
                                >
                                  <i className="fa fa-archive" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {total > 0 && (
                <div className="cr-pagination">
                  <span>
                    {t("claimsShowingCount", {
                      shown: reclamations.length,
                      total,
                    }) || `Showing ${reclamations.length} of ${total} reclamations`}
                  </span>
                  <div className="cr-pagination__btns">
                    {pageNumbers.map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`cr-page-btn${page === n ? " is-active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ClaimDetailsModal
        open={showDetailModal}
        reclamation={selectedReclamation}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedReclamation(null);
        }}
        t={t}
        extractClientName={extractClientName}
        extractDescriptionText={extractDescriptionText}
        buildWhatsAppLink={buildWhatsAppLink}
        renderTypeBadges={renderTypeBadges}
        renderStatusSelect={renderStatusSelect}
      />
    </>
  );
};

export default ClientReclamations;
